import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import Swal from "sweetalert2"

const formatBookingDateTime = value => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  })
}

const formatBookingType = value =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase())

const getBookingPurpose = booking => {
  const subtype = String(booking?.event_sub_type || "").toLowerCase()
  const eventType = String(booking?.event_type || "").toLowerCase()
  const combined = `${subtype} ${eventType}`

  if (combined.includes("talent") || combined.includes("scan")) return "TalentScan Test"
  if (combined.includes("screen") || combined.includes("call")) return "Screening Call"
  if (combined.includes("interview")) return "Interview"

  return formatBookingType(booking?.event_sub_type || booking?.event_type) || "Interview"
}

const InterviewBookingPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date(2026, 8, 17))
  const [selectedTime, setSelectedTime] = useState(null)
  const [saving, setSaving] = useState(false)
  const [booking, setBooking] = useState(null)
  const [eventInfo, setEventInfo] = useState(null)
  const [slots, setSlots] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [availabilityNotice, setAvailabilityNotice] = useState(null)
  const token = new URLSearchParams(window.location.search).get("token") || ""
  const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "")
  const bookingApi = axios.create({
    baseURL: configuredBaseUrl.endsWith("/api/v1")
      ? configuredBaseUrl
      : `${configuredBaseUrl}/api/v1`,
  })
  const apiTimezone = "Asia/Kolkata"

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!token) { setError("This interview booking link is invalid."); setLoading(false); return }
      setLoading(true); setError("")
      try {
        const availabilityResponse = await bookingApi.get(`/interview-booking/${encodeURIComponent(token)}/availability/`, { params: { timezone: apiTimezone } })
        const rawAvailability = availabilityResponse.data || {}
        const parsedAvailability = typeof rawAvailability === "string"
          ? JSON.parse(rawAvailability)
          : rawAvailability
        const availability = parsedAvailability?.data && !parsedAvailability?.slots
          ? parsedAvailability.data
          : parsedAvailability
        const existingBooking = availability?.booking || availability?.booking_details || (
          String(availability?.state || "").toLowerCase() === "booked" ? availability : null
        )
        if (existingBooking) {
          const confirmedBooking = { state: "booked", ...existingBooking }
          setBooking(confirmedBooking)
          return
        }
        setEventInfo(availability)
        const nextSlots = availability.slots || []
        setSlots(nextSlots)
        const isPending = String(availability.state || "").toLowerCase() === "uncertain"
          || String(availability.message || "").toLowerCase().includes("confirmation is pending")
        if (isPending) {
          setAvailabilityNotice(availability)
        }
        // CalendNow may return dates different from the initial calendar date.
        // Select the first available date so its time slots are visible immediately.
        if (nextSlots.length) {
          const [year, month, day] = nextSlots[0].date.split("-").map(Number)
          setSelectedDate(new Date(year, month - 1, day))
          setSelectedTime(null)
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail
            || err?.response?.data?.message
            || "Unable to load interview availability."
          setError("")
          await Swal.fire({
            icon: "error",
            title: "Unable to Load Availability",
            text: detail,
            confirmButtonText: "OK",
          })
        }
      } finally { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [token])

  const availableDates = useMemo(() => new Set(slots.map(slot => slot.date)), [slots])
  const selectedDateKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
  const selectedSlots = slots.filter(slot => slot.date === selectedDateKey)

  const monthLabel = selectedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  const calendarDays = useMemo(() => {
    const year = selectedDate.getFullYear()
    const month = selectedDate.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const previousDays = new Date(year, month, 0).getDate()
    return Array.from({ length: 35 }, (_, index) => {
      const dayNumber = index - firstDay + 1
      if (dayNumber < 1) return { day: previousDays + dayNumber, muted: true }
      if (dayNumber > daysInMonth) return { day: dayNumber - daysInMonth, muted: true }
      return { day: dayNumber, muted: false }
    })
  }, [selectedDate])

  const changeMonth = offset => {
    setSelectedDate(current => new Date(current.getFullYear(), current.getMonth() + offset, 17))
    setSelectedTime(null); setSlots([])
  }

  const dateText = selectedDate.toLocaleDateString("en-US", {
    weekday: "long", month: "2-digit", day: "2-digit", year: "numeric",
  })

  const handleSave = async () => {
      console.info("Save clicked", {
        token,
        selectedTime,
        selectedDate,
        apiUrl: `/interview-booking/${encodeURIComponent(token)}/`
      });
    if (!selectedTime || saving) return
    setSaving(true)
    try {
      const timeMatch = String(selectedTime).trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i)
      if (!timeMatch) throw new Error("Invalid time slot format")
      const [, hours, minutes, period] = timeMatch
      let hour = Number(hours)
      if (period) {
        if (period.toUpperCase() === "PM" && hour !== 12) hour += 12
        if (period.toUpperCase() === "AM" && hour === 12) hour = 0
      }
      const selectedDateValue = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
      const response = await bookingApi.post(
        `/interview-booking/${encodeURIComponent(token)}/`,
        {
          selected_date: selectedDateValue,
          selected_time: `${String(hour).padStart(2, "0")}:${minutes}`,
          timezone: apiTimezone,
        },
        { validateStatus: status => status < 500 }
      )
      const rawResult = response.data || {}
      const parsedResult = typeof rawResult === "string" ? JSON.parse(rawResult) : rawResult
      const result = parsedResult?.data && !parsedResult?.message ? parsedResult.data : parsedResult
      if (response.status === 409 || result?.error || result?.detail) {
        setSaving(false)
        await Swal.fire({
          icon: response.status === 409 ? "warning" : "error",
          title: response.status === 409 ? "Slot Already Booked" : "Booking Failed",
          text: String(result.error || result.detail || "Unable to save the booking."),
          confirmButtonText: "OK",
        })
        return
      }
      setSaving(false)
      const responseDetails = [
        result.scheduled_at && `Scheduled: ${formatBookingDateTime(result.scheduled_at)}`,
      ].filter(Boolean).join("\n")
      const alertText = [
        result.message || "Booking response received.",
        responseDetails,
      ].filter(Boolean).join("\n\n")

      const isPending = String(result.state || "").toLowerCase() === "uncertain"
        || String(result.message || "").toLowerCase().includes("confirmation is pending")

      if (result.state === "booked") {
        setBooking(current => ({ ...current, ...result }))
        await Swal.fire({ icon: "success", title: "Booking Confirmed", text: alertText, confirmButtonText: "OK" })
      } else if (isPending) {
        setBooking(null)
        await Swal.fire({ icon: "warning", title: "Booking Confirmation Pending", text: alertText, confirmButtonText: "OK" })
      } else {
        setBooking(null)
        await Swal.fire({ icon: "info", title: "Booking Response", text: alertText, confirmButtonText: "OK" })
      }
    } catch (error) {
      setSaving(false)
      const responseData = error?.response?.data
      const detail = responseData?.error
        || responseData?.detail
        || responseData?.message
        || (typeof responseData === "string" ? responseData : "Unable to save the booking.")
      await Swal.fire({
        icon: error?.response?.status === 409 ? "warning" : "error",
        title: error?.response?.status === 409 ? "Slot Already Booked" : "Booking Failed",
        text: String(detail),
        confirmButtonText: "OK",
      })
    } finally {
      setSaving(false)
    }
  }

  if (booking?.state === "booked") {
    const bookedAt = booking.scheduled_at ? new Date(booking.scheduled_at) : selectedDate
    return (
      <div className="interview-booking-page">
        <div className="booking-confirmation-card" role="status">
          <div className="booking-confirmation-icon">✓</div>
          <h1>Your Slot Is Booked Successfully</h1>
          <p className="booking-confirmation-type">
            {booking.booking_type === "talent_scan_test" ? "TalentScan test" : "Screening call"}
          </p>
          <div className="booking-confirmation-details">
            <strong>{bookedAt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</strong>
            <span>{bookedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
          </div>
          <p>{booking.booking_type === "talent_scan_test" ? "Your TalentScan test will be sent at the selected time." : "You will receive the screening call at the selected time."}</p>
        </div>
      </div>
    )
  }

  if (availabilityNotice) {
    return (
      <div className="interview-booking-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div className="booking-pending-card" role="status" style={{ width: "min(620px, 100%)", padding: "34px 38px", border: "1px solid #fde68a", borderRadius: 14, background: "#fffbeb", color: "#78350f", boxShadow: "0 12px 35px rgba(120, 53, 15, 0.10)", textAlign: "center" }}>
          <div className="booking-pending-icon" aria-hidden="true" style={{ width: 42, height: 42, margin: "0 auto 14px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f59e0b", color: "#fff", fontWeight: 800, fontSize: 18 }}>!</div>
          <h1 style={{ margin: "0 0 10px", fontSize: 24, color: "#92400e" }}>Booking Confirmation Pending</h1>
          <p style={{ margin: "0 0 22px", fontSize: 16, color: "#92400e" }}>{availabilityNotice.message || "Booking confirmation is pending."}</p>
          <div style={{ display: "grid", gap: 10, fontSize: 14, color: "#78350f" }}>
            {availabilityNotice.scheduled_at && <span><strong>Scheduled:</strong> {formatBookingDateTime(availabilityNotice.scheduled_at)}</span>}
            {availabilityNotice.booking_type && <span><strong>Type:</strong> {formatBookingType(availabilityNotice.booking_type)}</span>}
            {availabilityNotice.booking_id && <span><strong>Booking ID:</strong> {availabilityNotice.booking_id}</span>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="interview-booking-page">
      <div className="interview-booking-shell">
        <div className="interview-booking-content">
          <aside className="booking-profile-panel">
            <div className="booking-avatar">VT</div>
            <div className="booking-profile-label">Vitel</div>
            <h2>{getBookingPurpose(booking || eventInfo)}</h2>
            <div className="booking-profile-detail">◷ <span>30 min</span></div>
            <div className="booking-profile-detail">▣ <span>VitelMeet</span></div>
          </aside>
          <aside className="booking-calendar-panel">
            <div className="booking-kicker">SELECT A DATE</div>
            <div className="booking-month-row">
              <strong>{monthLabel}</strong>
            </div>
            <div className="booking-weekdays">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => <span key={day}>{day}</span>)}</div>
            <div className="booking-days">
              {calendarDays.map((item, index) => {
                const dateKey = !item.muted ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(item.day).padStart(2, "0")}` : ""
                const isSelected = !item.muted && item.day === selectedDate.getDate()
                const isAvailable = availableDates.has(dateKey)
                return <button key={`${item.day}-${index}`} type="button" disabled={item.muted || !isAvailable} className={`${item.muted ? "muted" : ""} ${isSelected ? "selected" : ""}`} onClick={() => { setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), item.day)); setSelectedTime(null) }}>{item.day}{isAvailable && <i />}</button>
              })}
            </div>
            <div className="booking-legend"><span><b className="dot selected-dot" /> SELECTED</span><span><b className="dot available-dot" /> AVAILABLE</span><span><b className="dot unavailable-dot" /> UNAVAILABLE</span></div>
          </aside>

          <main className="booking-time-panel">
            <div className="booking-kicker">CHOOSE TIME</div>
            <div className="booking-date-kicker">{selectedDate.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase()}</div>
            <h1>{dateText}</h1>
            {loading ? <p>Loading available times...</p> : availabilityNotice ? (
              <div className="booking-pending-card" role="status" style={{ maxWidth: 560, margin: "58px auto", padding: "28px 30px", border: "1px solid #fde68a", borderRadius: 12, background: "#fffbeb", color: "#78350f" }}>
                <div className="booking-pending-icon" aria-hidden="true" style={{ width: 36, height: 36, marginBottom: 12, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#f59e0b", color: "#fff", fontWeight: 800 }}>!</div>
                <h2 style={{ margin: "0 0 8px", fontSize: 19, color: "#92400e" }}>Booking Confirmation Pending</h2>
                <p style={{ margin: "0 0 18px", color: "#92400e" }}>{availabilityNotice.message || "Booking confirmation is pending."}</p>
                <div className="booking-pending-details" style={{ display: "grid", gap: 8, fontSize: 13, color: "#78350f" }}>
                  {availabilityNotice.scheduled_at && <span><strong>Scheduled:</strong> {formatBookingDateTime(availabilityNotice.scheduled_at)}</span>}
                  {availabilityNotice.booking_type && <span><strong>Type:</strong> {formatBookingType(availabilityNotice.booking_type)}</span>}
                  {availabilityNotice.booking_id && <span><strong>Booking ID:</strong> {availabilityNotice.booking_id}</span>}
                </div>
              </div>
            ) : <div className="booking-slots">{selectedSlots.map(slot => {
              const label = String(slot.time || "").trim()
              return <button type="button" key={`${slot.date}-${slot.time}`} className={selectedTime === slot.time ? "chosen" : ""} onClick={() => setSelectedTime(slot.time)}>{label}</button>
            })}</div>}
            {!availabilityNotice && (
              <button type="button" className="booking-save" disabled={!selectedTime || saving || loading || Boolean(booking?.state === "booked")} onClick={handleSave}>
                {saving ? <><span className="booking-save-spinner" aria-hidden="true" /> Saving...</> : booking?.state === "booked" ? "Booked" : "Save"}
              </button>
            )}
          </main>
        </div>
      </div>
    </div>
  )
}

export default InterviewBookingPage
